import {
  Badge,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Progress,
  Text,
  VStack,
} from '@chakra-ui/react';

import { formatFileSize, isDefined } from '@enterprise/core';

import { Check } from '#icons/check.tsx';
import { TrashAlt } from '#icons/trash-alt.tsx';
import { DefaultToolTip } from '#tool-tip/default-tool-tip.tsx';

import type { StagedFileRowProps } from './types/staged-file-row.props';

import { useStagedFileRowConfiguration } from './hooks/staged-file-row-configuration.hook';

/**
 * Renders a single staged file row in the upload queue.
 *
 * When `outputFilenameTemplate` is provided:
 * - The resolved output filename is shown as the primary name
 * - The original filename is shown below it as "Original filename: <n>"
 *
 * When `showSuffixInput` is true and the queue is in an editable state:
 * - A suffix input is rendered allowing the user to customize the per-file value
 * - When `staged.hasDuplicateSuffix` is true the input shows an inline error
 *
 * When no template is provided:
 * - The original filename is shown as the primary name with no secondary line
 */
export const StagedFileRow = ({
  disabled = false,
  fileIndex,
  filenameSuffixLabel,
  onRemove,
  onSuffixChange,
  outputFilenameTemplate,
  progress,
  showSuffixInput,
  staged,
  status,
  totalFiles,
}: StagedFileRowProps) => {
  const { canInteract, isDone, isProcessing, primaryName, resolvedName, showInput } = useStagedFileRowConfiguration({
    disabled,
    fileIndex,
    outputFilenameTemplate,
    progress,
    showSuffixInput,
    staged,
    status,
    totalFiles,
  });

  const compressionBadge = staged.willCompress ? (
    <Badge
      borderColor="gray.600"
      borderRadius="full"
      borderWidth={1}
      colorScheme="green"
      fontSize="10px"
      px={1.5}
      variant="subtle">
      gzip
    </Badge>
  ) : (
    <DefaultToolTip
      fontSize="sm"
      label={
        staged.skipReason === 'non-compressible-extension'
          ? 'Already-compressed format — sent as-is'
          : 'Below size threshold — sent as-is'
      }
      placement="right">
      <Badge
        borderColor="gray.600"
        borderRadius="full"
        borderWidth={1}
        colorScheme="blue"
        cursor="default"
        fontSize="10px"
        px={1.5}
        variant="subtle">
        raw
      </Badge>
    </DefaultToolTip>
  );

  return (
    <Box
      bg="gray.50"
      border="1px solid"
      borderColor={staged.hasDuplicateSuffix ? 'red.300' : 'gray.200'}
      borderRadius="lg"
      px={3}
      py={2.5}
      transition="border-color 0.2s">
      <Flex
        align="center"
        justify="space-between"
        mb={isProcessing ? 1.5 : 0}>
        <VStack
          align="flex-start"
          flex={1}
          minW={0}
          mr={2}
          spacing={0}>
          <DefaultToolTip label={primaryName}>
            <Text
              color="gray.800"
              fontSize="sm"
              fontWeight="medium"
              maxW="full"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap">
              {primaryName}
            </Text>
          </DefaultToolTip>
          <HStack spacing={2}>
            <Text
              color="gray.500"
              fontSize="sm">
              {formatFileSize(staged.file.size)}
            </Text>
            {compressionBadge}
          </HStack>
          {isDefined(resolvedName) && (
            <Text
              color="gray.400"
              fontSize="sm"
              mt={0.5}>
              Original filename: {staged.file.name}
            </Text>
          )}
        </VStack>

        {showInput && (
          <FormControl
            isInvalid={staged.hasDuplicateSuffix}
            maxW="80px"
            mr={2}
            w="auto">
            {isDefined(filenameSuffixLabel) && (
              <FormLabel
                color="gray.500"
                fontSize="10px"
                mb={0.5}>
                {filenameSuffixLabel}
              </FormLabel>
            )}
            <Input
              aria-label="Output filename value"
              bg="white"
              isDisabled={disabled}
              onChange={(e) => {
                onSuffixChange(staged.id, e.target.value);
              }}
              size="sm"
              value={staged.suffix}
            />
            {staged.hasDuplicateSuffix && (
              <FormErrorMessage
                fontSize="10px"
                mt={0.5}>
                Must be unique
              </FormErrorMessage>
            )}
          </FormControl>
        )}

        {isDone ? (
          <Badge
            borderRadius="full"
            colorScheme="green"
            fontSize="sm"
            px={1.5}
            variant="subtle">
            <Flex
              alignItems="center"
              gap={1}>
              <Check size="xs" />
              Done
            </Flex>
          </Badge>
        ) : canInteract ? (
          <IconButton
            alignItems="center"
            aria-label={`Remove ${staged.file.name}`}
            colorScheme="red"
            display="flex"
            icon={<TrashAlt />}
            isDisabled={disabled}
            onClick={() => {
              onRemove(staged.id);
            }}
            size="md"
            variant="ghost"
          />
        ) : null}
      </Flex>

      {isProcessing && (
        <Progress
          borderRadius="full"
          colorScheme="blue"
          hasStripe
          isAnimated
          mt={1.5}
          size="sm"
          value={progress ?? 0}
        />
      )}
    </Box>
  );
};
